import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import LongMenu from '@/components/LongMenu/LongMenu';
import {
  checkOrganizationFeatures,
  CHECK_TYPE_DB,
  isReturnedTransactionStatus,
  ORGANIZATION_FEATURES,
  REFUND_TYPES,
  SYSTEM_TAGS,
  utcToLocal,
  REFUND_PATH,
} from '@/utility';
import {
  ArrowDownward,
  ArrowUpward,
  HistoryRounded,
} from '@mui/icons-material';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow, { tableRowClasses } from '@mui/material/TableRow';
import { Base64 } from 'js-base64';
import moment from 'moment';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { toast } from 'react-toastify';
import useTranslatedTransactionStatuses from '@/hooks/useTranslatedTransactionStatuses';
import { useGetRefunds } from '../../../api/hooks';
import { RefundsApi } from '../../../api/clients/refunds/refunds.api';
import { PaymentsApi } from '../../../api/clients/payments/payments.api';
import { TransactionsApi } from '../../../api/clients/transactions/transactions.api';
import loader from '../../../assets/img/loader.svg';
import CancelPaymentDialog from '@/components/CancelPaymentDialog/CancelPaymentDialog';
import { CLAIM_CLIENT_URL, NEW_CLAIM_CLIENT_URL } from '../../../config';
import IssueCheckModal from '../IssueCheckModal';
import RefundInfoHistory from '../RefundInfoHistory';
import UpdateRefundModal from '../UpdateRefundModal';
import EditManualRefundModal from '../../Facilities/CreateOrEditManualRefund/EditManualRefundModal';
import { StatusHistoryModal } from './StatusHistory/StatusHistoryModal';
import { useLocation } from 'react-router-dom';
import CustomInput from '@/components/CustomInput/CustomInput';
import ConfirmationModal from '@/components/ConfirmationModal';
import { useConfirmation } from '@/context/ConfirmationContext';
import ECheckPreviewModal from '@/components/ECheckPreviewModal/ECheckPreviewModal';
import AmountWithCurrency from '@/components/AmountWithCurrency';
import FacilityTransactionFilters from './FacilityTransactionFilters';
import CustomerInfoCell from './components/CustomerInfoCell';
import PaymentCategoryStatusCell from './components/PaymentCategoryStatusCell';
import RefundStatusCell from './components/RefundStatusCell';

const StyledTableRow = styled(TableRow)(({ payment, role }) => {
  return {
    [`&.${tableRowClasses.root}`]: {
      backgroundColor:
        isReturnedTransactionStatus(payment.transaction_status) &&
        role === 'admin'
          ? '#eaa4a4'
          : undefined,
    },
  };
});

const refundTypeToLabel = {
  [REFUND_TYPES.REGULAR]: 'Consumer Payment',
  [REFUND_TYPES.INSURANCE]: 'Insurance Payment',
  [REFUND_TYPES.BUSINESS]: 'Business Payment',
};

export default function FacilityTransaction({ role }) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const isCustomPaymentTypesEnabled =
    role !== 'admin' &&
    posthog?.isFeatureEnabled('new-custom-payment-types-workflow');

  const { requestConfirmation } = useConfirmation();
  const { getTranslatedStatus } = useTranslatedTransactionStatuses();

  const [selectedRefund, setSelectedRefund] = useState(null);
  const [allowedRefundTypes, setAllowedRefundTypes] = useState([]);
  const [currentInfo, setCurrentInfo] = useState(null);
  const [originalInfo, setOriginalInfo] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [modalErr, setModalErr] = useState('');
  const [currentCheckRefund, setCurrentCheckRefund] = useState(null);
  const [selectedForTrackChanges, setSelectedForTrackChanges] = useState(null);
  const [selectedECheckRefundId, setSelectedECheckRefundId] = useState(null);

  const [infoHistory, setInfoHistory] = useState([]);

  const listInnerRef = useRef();
  const location = useLocation();

  useEffect(() => {
    const fetchAllowedRefundTypes = async () => {
      if (role !== 'admin') {
        const response = await RefundsApi.getAllowedRefundTypes();
        setAllowedRefundTypes(response.data.allowed_refund_types);
      }
    };
    fetchAllowedRefundTypes();
  }, [role]);

  const showBusinessUnits = useMemo(() => {
    return (
      role === 'admin' ||
      checkOrganizationFeatures(ORGANIZATION_FEATURES.showBusinessUnitsPage)
    );
  }, [role]);

  const showPayeeCategory = useMemo(() => {
    if (isCustomPaymentTypesEnabled) {
      return true;
    }
    return allowedRefundTypes.length > 1 || role === 'admin';
  }, [allowedRefundTypes, role, isCustomPaymentTypesEnabled]);

  const queryParams = new URLSearchParams(location.search);
  const quickFilterFromQuery = queryParams.get('quickFilter');

  const quickFilterDefaults = quickFilterFromQuery
    ? { quickFilter: quickFilterFromQuery }
    : {};

  const {
    payments,
    loading,
    setPartialState,
    startDate,
    endDate,
    payeeCategory,
    status,
    dateFilterType,
    facility,
    handleSearch,
    paymentMethod,
    subStatus,
    selectedBusinessUnit,
    transactionStatus,
    additionalInfo,
    offSet,
    hasMore,
    sortBy,
    refetchRefunds,
    search,
    selectedDataMappings,
    quickFilter,
    hasFilesAttached,
    customPaymentTypeId,
    facilityGroupId,
  } = useGetRefunds({
    refundLocation: REFUND_PATH.TRANSACTIONS,
    defaultValues: quickFilterDefaults,
  });

  useEffect(() => {
    if (currentCheckRefund) {
      setCurrentCheckRefund(
        payments?.find((payment) => payment.id === currentCheckRefund.id) ??
          null,
      );
    }
  }, [currentCheckRefund, payments]);

  const sortByColumn = (columnName) => {
    setPartialState({
      sortBy: {
        name: columnName,
        order: sortBy.name === columnName ? (sortBy.order === 1 ? -1 : 1) : 1,
      },
    });
  };

  const sortArrow = (columnName) => {
    return columnName === sortBy.name ? (
      sortBy.order === 1 ? (
        <ArrowDownward fontSize='small' />
      ) : sortBy.order === -1 ? (
        <ArrowUpward fontSize='small' />
      ) : sortBy.order === 0 ? (
        ''
      ) : (
        ''
      )
    ) : (
      ''
    );
  };

  const fetchStatusHistory = async () => {
    if (!selectedRefund) {
      setStatusHistory([]);
      return;
    }

    const fetchedStatusHistoryResponse = await RefundsApi.getStatusHistory(
      selectedRefund,
    );

    const fetchedStatusHistory = fetchedStatusHistoryResponse.data;
    if (fetchedStatusHistory.ach_data_backfill_error) {
      toast.error(fetchedStatusHistory.ach_data_backfill_error);
    }

    setStatusHistory(fetchedStatusHistory.history);
  };

  useEffect(() => {
    fetchStatusHistory();
  }, [selectedRefund]);

  const handleRetryPullingAch = async () => {
    fetchStatusHistory();
  };

  const isNewRefundeePortal = posthog?.isFeatureEnabled('new-refundee-portal');

  const copyLinkToClipBoard = (refund) => {
    const url = isNewRefundeePortal
      ? NEW_CLAIM_CLIENT_URL + '/claim/' + refund.uuid
      : CLAIM_CLIENT_URL + '/payments/' + Base64.encode(refund.id);
    navigator.clipboard.writeText(url);
    toast.success(t('payments.toast.copiedToClipboard'), { autoClose: 1000 });
  };
  const selectedPayments = payments.filter((payment) => payment.isChecked);

  const onInputChange = (e) => {
    if (e.target.type === 'text' || e.target.type === 'email') {
      setCurrentInfo((currentInfo) => {
        const info = { ...currentInfo };
        info[e.target.name] = e.target.value;
        return info;
      });
    }
    if (e.target.type === 'checkbox') {
      setCurrentInfo((currentInfo) => {
        const info = { ...currentInfo };
        info[e.target.name] = e.target.checked;
        return info;
      });
    }
  };
  const processPayment = async ({ id, skipCheck = false, checkType }) => {
    try {
      const encodedId = Base64.encode(id);
      await TransactionsApi.addCheckFacility(encodedId, skipCheck, checkType);
      setCurrentCheckRefund(null);
      refetchRefunds();

      if (checkType === CHECK_TYPE_DB.ECHECK) {
        requestConfirmation(
          t('payments.messages.echeckProcessStarted'),
          () => {},
        );
      }
    } catch (error) {
      console.error(
        'Error processing payment:',
        error.response?.data?.message,
        error.response?.status,
      );
      if (
        error.response?.status === 400 &&
        error.response?.data?.invalidAddress === true
      ) {
        const message = (
          <div>
            {t('payments.addressValidation.failed')}{' '}
            {error.response?.data?.invalidAddressMessage}
            <br />
            <br />
            {t('payments.addressValidation.proceedWarning')}
          </div>
        );
        requestConfirmation(message, () =>
          processPayment({ id, skipCheck: true, checkType }),
        );
      } else {
        console.error('Error processing payment:', error);
      }
    }
  };

  const getInfoHistory = async (id, refund) => {
    try {
      const result = await RefundsApi.getRefundInfoHistory(id);
      const infoHistoryArray = [];
      let lastRefundInfoIndex;

      const lastRefundInfo = result.data
        .filter((info) => info.type === 'refund_info')
        .at(-1);

      lastRefundInfoIndex = result.data.findIndex(
        (info) => lastRefundInfo?.id === info.id,
      );

      result.data.forEach((info, index) => {
        if (info.type === 'refund_attachments') {
          const oldFiles = info.old_info ?? [];
          const newFiles = info.new_info ?? [];
          const newFilesKeys = newFiles.map((file) => file.aws_key);
          const oldFilesKeys = oldFiles.map((file) => file.aws_key);

          const filesAdded = newFiles.filter(
            (file) => !oldFilesKeys.includes(file.aws_key),
          );

          const filesRemoved = oldFiles.filter(
            (file) => !newFilesKeys.includes(file.aws_key),
          );

          infoHistoryArray.push({
            filesAdded,
            filesRemoved,
            type: info.type,
            updated_by: `${info.updated_by} on ${moment(info.created_at).format(
              'YYYY-MM-DD HH:mm:ss',
            )}`,
            updated_at_location: info.updated_at_location,
          });
        }

        infoHistoryArray.push({
          first_name: info.new_info.first_name,
          last_name: info.new_info.last_name,
          email: info.new_info.email,
          address: `${info.new_info.refund_to_address_line1},
${info.new_info.refund_to_address_line2},
${info.new_info.refund_to_city},
${info.new_info.refund_to_state}, ${info.new_info.refund_to_zip}.`,
          comment: info.comment ?? 'N/A',
          updated_by: `${info.updated_by} on ${moment(info.created_at).format(
            'YYYY-MM-DD HH:mm:ss',
          )}`,
          type: info.type,
          id: info.id,
          memo: info.new_info.memo,
          business_payee_name: info.new_info.business_payee_name,
          updated_at_location: info.updated_at_location,
        });

        if (lastRefundInfoIndex === index) {
          infoHistoryArray.push({
            first_name: info.old_info.first_name,
            last_name: info.old_info.last_name,
            email: info.old_info.email,
            address: `${info.old_info.refund_to_address_line1},
${info.old_info.refund_to_address_line2},
${info.old_info.refund_to_city},
${info.old_info.refund_to_state}, ${info.old_info.refund_to_zip}.`,
            comment: 'N/A',
            updated_by: 'N/A',
            type: info.type,
            id: info.id,
            memo: info.old_info.memo,
            business_payee_name: info.old_info.business_payee_name,
            updated_at_location: 'N/A',
          });
        }
      });

      const refundInfoLength = infoHistoryArray.filter(
        (info) => info.type === 'refund_info',
      ).length;

      if (!refundInfoLength) {
        infoHistoryArray.push({
          first_name: refund.first_name,
          last_name: refund.last_name,
          email: refund.email,
          address: `${refund.refund_to_address_line1},
${refund.refund_to_address_line2},
${refund.refund_to_city},
${refund.refund_to_state}, ${refund.refund_to_zip}.`,
          comment: 'N/A',
          updated_by: 'N/A',
          type: 'refund_info',
          memo: refund.memo,
          business_payee_name: refund.business_payee_name,
          updated_at_location: 'N/A',
        });
      }

      setInfoHistory(infoHistoryArray);
    } catch (err) {
      console.log(err);
    }
  };

  const handleScroll = async () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      // INFO: consider margin of error, 10 px
      const sumPx = scrollTop + clientHeight;
      const margin = 10;
      if (sumPx > scrollHeight - margin && sumPx < scrollHeight + margin) {
        if (hasMore && !loading) {
          setPartialState({ offSet: offSet + 25 });
        }
      }
    }
  };

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelComment, setCancelComment] = useState('');
  const [cancelRefundId, setCancelRefundId] = useState(null);

  const confirmCancelRefund = async () => {
    if (!cancelRefundId || !cancelComment) return;
    try {
      await RefundsApi.cancelRefund(cancelRefundId, cancelComment);
      setCancelModalOpen(false);
      setCancelComment('');
      setCancelRefundId(null);
      refetchRefunds();
    } catch (err) {
      console.log('Error canceling refund:', err);
    }
  };

  const [cancelPaymentModalOpen, setCancelPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const handleCancelPaymentConfirm = async (comment) => {
    await PaymentsApi.cancelPayment(selectedPayment.id, comment);
    toast.success(t('payments.messages.cancelPaymentSuccess'));
    setCancelPaymentModalOpen(false);
    setSelectedPayment(null);
    refetchRefunds();
  };

  const statusAction = async ({ path, id, comment }) => {
    if (path === 'cancelPayment') {
      const payment = payments.find((r) => r.id === id);
      setSelectedPayment(payment);
      setCancelPaymentModalOpen(true);
      return;
    }
    if (path.includes('cancel')) {
      setCancelRefundId(id);
      setCancelModalOpen(true);
      return;
    }
    await RefundsApi.statusAction(id, path, comment);
    refetchRefunds();
  };

  const deleteOrRestorePayment = async (refund) => {
    if (refund.is_deleted) {
      await PaymentsApi.restorePayment(refund.id);
    } else {
      await PaymentsApi.deletePayment(refund.id);
    }

    refetchRefunds();
  };

  return (
    <div>
      <Card>
        <CardHeader
          style={{ display: 'flex', justifyContent: 'space-between' }}
          color='primary'>
          <h4>{t('payments.title')} </h4>
        </CardHeader>

        <CardBody>
          <div
            ref={listInnerRef}
            style={{ maxHeight: '75vh', overflow: 'auto' }}
            onScroll={handleScroll}>
            {loading && (
              <div className='loaders'>
                <img src={loader} />
              </div>
            )}

            <FacilityTransactionFilters
              role={role}
              isCustomPaymentTypesEnabled={isCustomPaymentTypesEnabled}
              allowedRefundTypes={allowedRefundTypes}
              payeeCategory={payeeCategory}
              customPaymentTypeId={customPaymentTypeId}
              status={status}
              subStatus={subStatus}
              additionalInfo={additionalInfo}
              paymentMethod={paymentMethod}
              transactionStatus={transactionStatus}
              facility={facility}
              facilityGroupId={facilityGroupId}
              selectedBusinessUnit={selectedBusinessUnit}
              dateFilterType={dateFilterType}
              startDate={startDate}
              endDate={endDate}
              search={search}
              quickFilter={quickFilter}
              selectedDataMappings={selectedDataMappings}
              hasFilesAttached={hasFilesAttached}
              sortBy={sortBy}
              setPartialState={setPartialState}
              handleSearch={handleSearch}
            />
            <div>
              <div className='t-header-right'>
                {selectedPayments.length ? (
                  <h4 style={{ margin: '-20px 0 0 0' }}>
                    {t('payments.selection.summary', {
                      count: selectedPayments.length,
                      amount: parseFloat(
                        selectedPayments.reduce(
                          (prev, cur) => prev + cur.refund_amount,
                          0,
                        ),
                      ).toFixed(2),
                    })}
                  </h4>
                ) : (
                  ''
                )}
              </div>
            </div>
            <Table width='100%' id='template'>
              <TableHead>
                <TableRow style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <TableCell onClick={() => sortByColumn('created_at')}>
                    {t('payments.table.dateProcessed')}{' '}
                    {sortArrow('created_at')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('status_updatedat')}>
                    {t('payments.table.lastUpdated')}{' '}
                    {sortArrow('status_updatedat')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('last_name')}>
                    {t('payments.table.customerInfo')} {sortArrow('last_name')}
                  </TableCell>

                  <TableCell onClick={() => sortByColumn('facility_name')}>
                    {t('payments.table.facility')}
                    {sortArrow('facility_name')}
                  </TableCell>

                  <TableCell onClick={() => sortByColumn('refund_amount')}>
                    {t('payments.table.amount')}
                    {sortArrow('refund_amount')}
                  </TableCell>
                  {showPayeeCategory && (
                    <TableCell onClick={() => sortByColumn('refund_type')}>
                      {t('payments.table.payeeCategory')}
                      {sortArrow('refund_type')}
                    </TableCell>
                  )}
                  <TableCell
                    onClick={() => sortByColumn('payee_account_number')}>
                    {t('payments.table.customerIds')}
                    {sortArrow('payee_account_number')}
                  </TableCell>
                  {showBusinessUnits && (
                    <TableCell
                      onClick={() => sortByColumn('business_unit_name')}>
                      {t('payments.table.businessUnit')}
                      {sortArrow('business_unit_name')}
                    </TableCell>
                  )}
                  <TableCell>
                    {t('payments.table.paymentCategoryStatus')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('refund_status')}>
                    {t('payments.table.refundStatus')}
                    {sortArrow('refund_status')}
                  </TableCell>
                  <TableCell>{t('payments.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              {payments && payments.length > 0 ? (
                <TableBody>
                  {payments.map((refund, index) => (
                    <StyledTableRow
                      payment={refund}
                      role={role}
                      className='txtalign'
                      data-testid={`transactionRow-${refund.id}`}
                      style={{
                        backgroundColor:
                          role === 'admin' && refund.is_deleted
                            ? '#FFFACD'
                            : (refund.is_error ||
                                refund.transaction_is_error) &&
                              role === 'admin'
                            ? '#eaa4a4'
                            : '',
                      }}
                      key={index}>
                      <TableCell>
                        {utcToLocal(refund.created_at, 'MM/DD/YYYY')}
                        <br />
                        <span title={refund.refund_file_name}>
                          <Typography fontSize={'14px'}>
                            {refund.tags?.includes(SYSTEM_TAGS.MANUAL_ENTRY)
                              ? t('payments.table.manuallyCreated')
                              : refund.refund_file_name
                              ? `${refund.refund_file_name.slice(
                                  0,
                                  7,
                                )}...${refund.refund_file_name.slice(-7)}`
                              : 'N/A'}
                          </Typography>
                        </span>
                      </TableCell>
                      <TableCell>
                        {refund.status_updatedat
                          ? utcToLocal(refund.status_updatedat, 'MM/DD/YYYY')
                          : 'N/A'}
                      </TableCell>
                      <CustomerInfoCell
                        refund={refund}
                        role={role}
                        isCustomPaymentTypesEnabled={
                          isCustomPaymentTypesEnabled
                        }
                        onEditClick={() => {
                          setCurrentInfo(refund);
                          setOriginalInfo(refund);
                          setModalErr('');
                        }}
                        onSendPaymentClick={() => {
                          setCurrentCheckRefund(refund);
                        }}
                        onViewECheckClick={() =>
                          setSelectedECheckRefundId(refund.id)
                        }
                      />

                      <TableCell>{refund.facility_name}</TableCell>

                      <TableCell
                        data-testid={`transactionRowAmount-${refund.id}`}>
                        <AmountWithCurrency
                          amount={refund.refund_amount}
                          currency={refund.currency}
                        />
                      </TableCell>
                      {showPayeeCategory && (
                        <TableCell
                          data-testid={`transactionRowCategory-${refund.id}`}>
                          {role === 'admin'
                            ? refund.custom_payment_type ||
                              refundTypeToLabel[refund.refund_type] ||
                              'N/A'
                            : isCustomPaymentTypesEnabled
                            ? refund.custom_payment_type || 'N/A'
                            : refundTypeToLabel[refund.refund_type] || 'N/A'}
                          <br />({refund.entity_type || 'N/A'})
                        </TableCell>
                      )}
                      <TableCell
                        data-testid={`transactionRowCpdId-${refund.id}`}>
                        {refund.payee_account_number || 'N/A'} <br />
                        {refund.client_transaction_id || 'N/A'} <br />
                        {refund.id || 'N/A'}
                      </TableCell>
                      {showBusinessUnits && (
                        <TableCell>
                          {refund.business_unit_name || 'N/A'} <br />
                          {refund.business_unit_identifier || 'N/A'}
                        </TableCell>
                      )}
                      <PaymentCategoryStatusCell
                        refund={refund}
                        role={role}
                        getTranslatedStatus={getTranslatedStatus}
                      />
                      <RefundStatusCell refund={refund} />
                      <TableCell>
                        <div
                          style={{
                            display: 'flex',
                            gap: '2px',
                            alignItems: 'center',
                          }}>
                          <HistoryRounded
                            data-testid={`statusHistoryButton-${refund.id}`}
                            style={{ color: '#3AB795' }}
                            title='Status History'
                            onClick={() => setSelectedRefund(refund.id)}
                          />

                          <LongMenu
                            type={role}
                            handleCopyToClipboard={() =>
                              copyLinkToClipBoard(refund)
                            }
                            handleRefundHistory={() => {
                              setSelectedForTrackChanges(refund);
                              getInfoHistory(refund.id, refund);
                            }}
                            statusAction={statusAction}
                            deleteOrRestorePayment={() =>
                              deleteOrRestorePayment(refund)
                            }
                            refund={refund}
                            sendCheck={() => {
                              setCurrentCheckRefund(refund);
                            }}
                          />
                        </div>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {t('payments.table.noTransactionDataFound')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </div>
        </CardBody>
      </Card>
      <StatusHistoryModal
        role={role}
        open={selectedRefund !== null}
        statusHistory={statusHistory}
        onRetryPullingAch={handleRetryPullingAch}
        onClose={() => setSelectedRefund(null)}
        refund={payments.find((r) => r.id === selectedRefund)}
      />
      {currentCheckRefund && (
        <IssueCheckModal
          currentCheckRefund={currentCheckRefund}
          setCurrentCheckRefund={setCurrentCheckRefund}
          setCurrentInfo={setCurrentInfo}
          setOriginalInfo={setOriginalInfo}
          setModalErr={setModalErr}
          processPayment={processPayment}
          setPartialState={setPartialState}
        />
      )}

      {currentInfo &&
        (isCustomPaymentTypesEnabled ? (
          <EditManualRefundModal
            refundLocation={REFUND_PATH.TRANSACTIONS}
            refund={currentInfo}
            onClose={() => {
              setCurrentInfo(null);
              setOriginalInfo(null);
              setModalErr('');
            }}
            updateStatus={false}
            reFetch={refetchRefunds}
            resendCommunicationUi={true}
            role={role}
          />
        ) : (
          <UpdateRefundModal
            setModalErr={setModalErr}
            currentInfo={currentInfo}
            originalInfo={originalInfo}
            modalErr={modalErr}
            onInputChange={onInputChange}
            setCurrentInfo={setCurrentInfo}
            setOriginalInfo={setOriginalInfo}
            role={role}
            getRefunds={refetchRefunds}
            payments={payments}
            currentCheckRefund={currentCheckRefund}
            setCurrentCheckRefund={setCurrentCheckRefund}
          />
        ))}
      {selectedForTrackChanges && (
        <RefundInfoHistory
          selectedForTrackChangesId={selectedForTrackChanges.id}
          infoHistory={infoHistory}
          entityType={selectedForTrackChanges.entity_type}
          onClose={() => {
            setSelectedForTrackChanges(null);
          }}
        />
      )}
      <ConfirmationModal
        open={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setCancelComment('');
          setCancelRefundId(null);
        }}
        onConfirm={confirmCancelRefund}>
        <div>
          <div>{t('payments.messages.cancelRefundConfirmation')}</div>
          <div style={{ marginTop: 10 }}>
            <CustomInput
              labelText={
                <>
                  {t('payments.fields.comment')}
                  <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                </>
              }
              id='comment'
              name='comment'
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
              formControlProps={{ fullWidth: true }}
              error={!cancelComment}
              inputProps={{
                helperText: !cancelComment
                  ? t('payments.validation.commentRequired')
                  : '',
              }}
              helperTextStyle={{ color: 'red' }}
            />
          </div>
        </div>
      </ConfirmationModal>

      <CancelPaymentDialog
        open={cancelPaymentModalOpen}
        onClose={() => {
          setCancelPaymentModalOpen(false);
          setSelectedPayment(null);
        }}
        onConfirm={handleCancelPaymentConfirm}
        paymentId={selectedPayment?.id}
        organizationId={selectedPayment?.organization_id}
        payment={selectedPayment}
      />

      <ECheckPreviewModal
        open={selectedECheckRefundId !== null}
        onClose={() => setSelectedECheckRefundId(null)}
        refund={payments?.find((p) => p.id === selectedECheckRefundId)}
        reFetch={refetchRefunds}
      />
    </div>
  );
}
